import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { validateEmail } from '../utils/validation';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../components/Common/Logo';
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    School,
    BookOpen,
    Users,
    Award,
    TrendingUp,
    ArrowLeft,
    KeyRound,
    AlertTriangle,
    Timer,
    RefreshCw,
    ShieldCheck,
    X,
    Send,
    CheckCircle
} from 'lucide-react';

const Login = () => {
    const navigate = useNavigate();
    const { login, verifyOtp } = useAuth();

    // View state: 'login' | 'verify' | 'forgot' | 'reset'
    const [view, setView] = useState('login');

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    // Login 2FA State
    const [loginOtp, setLoginOtp] = useState('');

    // Forgot/Reset Password State
    const [resetEmail, setResetEmail] = useState('');
    const [resetOtp, setResetOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    // Remember Me removed - forcing session-only storage for security

    // Inline error state (prevents page reload - no native browser validation)
    const [loginError, setLoginError] = useState('');

    // New State for Improvements
    const [capsLockOn, setCapsLockOn] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const [showStudentAlert, setShowStudentAlert] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [contactForm, setContactForm] = useState({ name: '', email: '', category: '', priority: 'medium', description: '' });
    const [contactAttachments, setContactAttachments] = useState([]);
    const [contactErrors, setContactErrors] = useState({});
    const [contactSubmitting, setContactSubmitting] = useState(false);
    const [contactSubmitted, setContactSubmitted] = useState(false);

    const primaryButtonClass = 'w-full bg-gradient-to-r from-[#002147] via-[#1a3884] to-[#3ab5e6] text-white font-bold py-3 rounded-2xl shadow-[0_18px_40px_-20px_rgba(26,56,132,0.65)] hover:shadow-[0_20px_45px_-18px_rgba(26,56,132,0.72)] transform hover:scale-[1.01] active:scale-[0.985] transition-all duration-200 disabled:opacity-50';
    const fieldClass = 'w-full pl-12 pr-4 py-2.5 bg-[#f8fbff]/90 dark:bg-[#001f40]/90 border-2 border-[#d7e5f5] dark:border-[#1a3884]/65 rounded-2xl text-gray-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-[#4f7dff] dark:focus:border-[#4f7dff] focus:ring-4 focus:ring-[#dbeafe] dark:focus:ring-[#1a3884]/30 transition-all duration-200';

    // Student alert: show immediately and keep visible
    useEffect(() => {
        const timer = setTimeout(() => setShowStudentAlert(true), 3000);
        return () => clearTimeout(timer);
    }, []);

    // Timer Effect
    useEffect(() => {
        let interval;
        if (otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer((prev) => prev - 1);
            }, 1000);
        } else if (otpTimer === 0 && (view === 'reset' || view === 'verify')) {
            setCanResend(true);
        }
        return () => clearInterval(interval);
    }, [otpTimer, view]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleKeyDown = (e) => {
        if (e.getModifierState && e.getModifierState('CapsLock')) {
            setCapsLockOn(true);
        } else {
            setCapsLockOn(false);
        }
    };

    const getPasswordStrength = (pass) => {
        if (!pass) return 0;
        let score = 0;
        if (pass.length >= 8) score++;
        if (/[a-z]/.test(pass)) score++;
        if (/[A-Z]/.test(pass)) score++;
        if (/[0-9]/.test(pass)) score++;
        if (/[^a-zA-Z0-9]/.test(pass)) score++;
        return score; // 0-5
    };

    const handleNavigation = (role) => {
        setTimeout(() => {
            switch (role) {
                case 'admin':
                    navigate('/admin/dashboard');
                    break;
                case 'college_admin':
                    navigate('/college/dashboard');
                    break;
                case 'student':
                    navigate('/student/dashboard');
                    break;
                case 'support':
                    navigate('/support/dashboard');
                    break;
                case 'recruiter':
                    navigate('/recruiter/dashboard');
                    break;
                case 'office_admin':
                    navigate('/colleges');
                    break;
                default:
                    navigate('/dashboard');
            }
        }, 300);
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setLoginError(''); // Clear previous errors

        // 1. Frontend Validation (custom - no browser native popup / reload)
        if (!formData.email.trim()) {
            return setLoginError('Email address is required.');
        }
        if (!validateEmail(formData.email.trim())) {
            return setLoginError('Please enter a valid email address.');
        }
        if (!formData.password.trim()) {
            return setLoginError('Password is required.');
        }

        setLoading(true);

        try {
            // Note: login now returns { requireOtp: true, ... } if 2FA needed
            const response = await login(formData.email, formData.password);

            if (response.requireOtp) {
                toast.success(response.message);
                setLoginError('');
                setView('verify');
                setOtpTimer(60);
                setCanResend(false);
            } else if (response.success) {
                // Direct login fallback
                setLoginError('');
                handleNavigation(response.user.role);
            } else {
                // Show error inline instead of just toast to avoid confusion
                setLoginError(response.message || 'Invalid email or password. Please try again.');
                toast.error(response.message || 'Login failed');
            }
        } catch (error) {
            const msg = 'Something went wrong. Please try again.';
            setLoginError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifySubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Pass true for rememberMe to force persistence
            const response = await verifyOtp(formData.email, loginOtp, false); // Always use sessionStorage
            if (response.success) {
                handleNavigation(response.user.role);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Verification failed");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();

        if (!validateEmail(resetEmail.trim())) {
            return toast.error("Please enter a valid email address");
        }

        setLoading(true);
        try {
            const { data } = await api.post('/auth/forgotpassword', { email: resetEmail });
            if (data.success) {
                toast.success(data.message);
                setView('reset');
                setOtpTimer(60); // Start 60s timer
                setCanResend(false);
            }
        } catch (error) {
            if (error.response?.status === 429) {
                toast.error("Too many requests. Please wait a while.");
            } else {
                toast.error(error.response?.data?.message || 'Failed to send OTP');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (view === 'verify' && !formData.password) {
            toast.error('Cannot resend OTP — session expired. Please log in again.');
            setView('login');
            return;
        }
        setLoading(true);
        try {
            if (view === 'verify') {
                const response = await login(formData.email, formData.password);
                if (response.requireOtp || response.success) {
                    toast.success('OTP Resent!');
                    setOtpTimer(60);
                    setCanResend(false);
                } else {
                    toast.error(response.message || 'Failed to resend OTP');
                }
            } else {
                // Forgot password resend
                const { data } = await api.post('/auth/forgotpassword', { email: resetEmail });
                if (data.success) {
                    toast.success('OTP Resent successfully!');
                    setOtpTimer(60);
                    setCanResend(false);
                }
            }
        } catch (error) {
            toast.error('Failed to resend OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleResetSubmit = async (e) => {
        e.preventDefault();

        // Validate password requirements
        const errors = [];
        if (newPassword.length < 8) {
            errors.push("at least 8 characters");
        }
        if (!/[a-z]/.test(newPassword)) {
            errors.push("lowercase letter");
        }
        if (!/[A-Z]/.test(newPassword)) {
            errors.push("uppercase letter");
        }
        if (!/[0-9]/.test(newPassword)) {
            errors.push("number");
        }
        if (!/[^a-zA-Z0-9]/.test(newPassword)) {
            errors.push("symbol");
        }

        if (errors.length > 0) {
            return toast.error(`Password must include: ${errors.join(", ")}`);
        }

        if (newPassword !== confirmPassword) {
            return toast.error("Passwords do not match");
        }

        setLoading(true);
        try {
            const { data } = await api.put('/auth/resetpassword', {
                email: resetEmail,
                otp: resetOtp,
                newPassword
            });
            if (data.success) {
                toast.success('Password reset successfully! Please login.');
                setView('login');
                // Clear reset fields
                setResetOtp('');
                setNewPassword('');
                setConfirmPassword('');
                setResetEmail('');
                setOtpTimer(0);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };
    const handleContactSubmit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!contactForm.name.trim()) errs.name = 'Name is required';
        if (!contactForm.email.trim()) errs.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) errs.email = 'Invalid email';
        if (!contactForm.category) errs.category = 'Please select a category';
        if (!contactForm.description.trim() || contactForm.description.length < 10) errs.description = 'Description must be at least 10 characters';
        setContactErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setContactSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('contactName', contactForm.name);
            fd.append('contactEmail', contactForm.email);
            fd.append('category', contactForm.category);
            fd.append('title', `[Guest] ${contactForm.category} - ${contactForm.name}`);
            fd.append('description', contactForm.description);
            fd.append('priority', contactForm.priority);
            contactAttachments.forEach(file => fd.append('attachments', file));
            await api.post('/tickets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setContactSubmitted(true);
            setTimeout(() => {
                setShowContactModal(false);
                setContactSubmitted(false);
                setContactForm({ name: '', email: '', category: '', priority: 'medium', description: '' });
                setContactAttachments([]);
            }, 2500);
        } catch (err) {
            setContactErrors(prev => ({ ...prev, submit: err.response?.data?.message || 'Failed to submit. Please try again.' }));
        } finally {
            setContactSubmitting(false);
        }
    };

    return (
        <>
        <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(79,125,255,0.18),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(58,181,230,0.16),_transparent_30%),linear-gradient(135deg,_#f8fbff_0%,_#edf4fb_52%,_#f7fbff_100%)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(26,56,132,0.4),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(58,125,255,0.1),_transparent_40%),linear-gradient(135deg,_#001124_0%,_#00152e_52%,_#001b3a_100%)] flex items-center justify-center py-6 md:py-8 px-4 md:px-6 lg:px-8 relative overflow-y-auto">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-[#9fc5ff] rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-blob"></div>
                <div className="absolute top-40 right-10 w-72 h-72 bg-[#8ce3f5] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-8 left-20 w-72 h-72 bg-[#c7d2fe] rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            {/* Floating Icons */}
            <div className="absolute top-10 left-10 text-[#1a3884] opacity-20 animate-float">
                <School size={48} />
            </div>
            <div className="absolute top-20 right-20 text-[#3ab5e6] opacity-20 animate-float animation-delay-1000">
                <BookOpen size={40} />
            </div>
            <div className="absolute bottom-20 left-20 text-[#3ab5e6] opacity-20 animate-float animation-delay-2000">
                <Award size={44} />
            </div>
            <div className="absolute bottom-10 right-10 text-[#1a3884] opacity-20 animate-float animation-delay-3000">
                <Users size={36} />
            </div>

            {/* Main Content */}
            <div className="relative z-10 w-full max-w-5xl grid md:grid-cols-2 gap-6 items-center">
                {/* Left Side - Branding & Info */}
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className="hidden md:block text-center md:text-left space-y-4 px-4"
                >
                    <div className="inline-block transform hover:scale-[1.02] transition-transform duration-200">
                        <Logo withBackground={true} size="md" />
                    </div>

                    <div className="space-y-4 max-w-xl">
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                            Admin Control
                            <span className="block bg-gradient-to-r from-[#002147] via-[#1a3884] to-[#3ab5e6] dark:from-[#60a5fa] dark:via-[#a5b4fc] dark:to-[#3ab5e6] bg-clip-text text-transparent">
                                Panel
                            </span>
                        </h2>
                        <p className="text-sm lg:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                            Manage institutions, monitor learning activity, handle escalations, and maintain full oversight of the SMAART Institute platform.
                        </p>
                    </div>

                    {/* Admin Feature Cards */}
                    <div className="grid grid-cols-2 gap-4 mt-5">
                        {[
                            { icon: School, title: 'Manage Colleges & Students', desc: 'Onboard & oversee institutions & Students', color: 'from-[#00152E] to-[#1a3884]' },
                            { icon: Users, title: 'User Management', desc: 'Admins, IT-Supports, Recruiter', color: 'from-[#00152E] to-[#1a3884]' },
                            { icon: TrendingUp, title: 'Platform Analytics', desc: 'Real-time insights', color: 'from-[#0f766e] to-[#3ab5e6]' },
                            { icon: ShieldCheck, title: 'Secure Access', desc: '2FA protected login', color: 'from-[#00152E] to-[#1a3884]' }
                        ].map((feature, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 + (idx * 0.1) }}
                                className="bg-white/72 dark:bg-[#002147]/72 backdrop-blur-sm rounded-2xl p-3.5 lg:p-4 border border-[#dbe7f3] dark:border-[#1a3884]/45 shadow-[0_20px_45px_-32px_rgba(15,23,42,0.4)] dark:shadow-[0_20px_45px_-32px_rgba(0,0,0,0.6)] hover:shadow-[0_24px_50px_-28px_rgba(26,56,132,0.28)] transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className={`w-9 h-9 lg:w-10 lg:h-10 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-2 shadow-md`}>
                                    <feature.icon className="text-white" size={18} />
                                </div>
                                <h3 className="text-sm lg:text-base font-semibold text-gray-900 dark:text-white mb-0.5">{feature.title}</h3>
                                <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-300 leading-tight">{feature.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Right Side - Login/Forgot/Reset Form */}
                <div className="bg-white/82 dark:bg-[#002147]/82 backdrop-blur-xl rounded-[2rem] shadow-[0_30px_80px_-38px_rgba(15,23,42,0.45)] dark:shadow-[0_30px_80px_-38px_rgba(0,0,0,0.6)] p-6 md:p-8 border border-[#dbe7f3] dark:border-[#1a3884]/45 md:min-h-[500px] flex flex-col justify-center relative overflow-hidden">
                    <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#002147] via-[#1a3884] to-[#3ab5e6]"></div>
                    <AnimatePresence mode="wait">
                        {/* View: Login */}
                        {view === 'login' && (
                            <motion.div
                                key="login"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="w-full"
                            >
                                {/* Mobile Logo */}
                                <div className="md:hidden flex items-center justify-center mb-8 transform hover:scale-[1.02] transition-transform duration-200">
                                    <Logo withBackground={true} size="sm" align="center" />
                                </div>

                                <div className="text-center mb-3">
                                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-1">Welcome Back!</h2>

                                    <div className="my-2">
                                        <div className="bg-[#eef6ff] dark:bg-[#1a3884]/30 border border-[#d5e6fb] dark:border-[#1a3884]/60 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 mb-1.5 shadow-sm">
                                            <ShieldCheck size={14} className="text-[#1a3884] dark:text-[#60a5fa]" />
                                            <span className="text-[#1a3884] dark:text-[#60a5fa] text-[10.5px] font-bold uppercase tracking-wide">Administration Portal</span>
                                        </div>
                                        <p className="text-slate-500 dark:text-slate-400 text-[13px] px-2 leading-5">
                                            Secure access for College Admins, System Admins, IT-Supports & Recruiters.
                                        </p>
                                    </div>
                                </div>

                                <form onSubmit={handleLoginSubmit} className="space-y-4 lg:space-y-5" noValidate>
                                    {/* Inline Error Banner */}
                                    {loginError && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"
                                        >
                                            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-red-500" />
                                            <span>{loginError}</span>
                                        </motion.div>
                                    )}

                                    {/* Email Input */}
                                    <div className="space-y-1">
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wide">Email</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Mail className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type="text"
                                                name="email"
                                                value={formData.email}
                                                onChange={(e) => { handleChange(e); setLoginError(''); }}
                                                disabled={loading}
                                                placeholder="Enter your email"
                                                autoComplete="email"
                                                className={`${fieldClass} ${loginError ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''
                                                    }`}
                                            />
                                        </div>
                                    </div>

                                    {/* Password Input */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between">
                                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wide">Password</label>
                                            {capsLockOn && (
                                                <span className="text-xs text-amber-500 flex items-center font-semibold animate-pulse">
                                                    <AlertTriangle size={14} className="mr-1" /> Caps Lock is ON
                                                </span>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Lock className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={formData.password}
                                                onChange={(e) => { handleChange(e); setLoginError(''); }}
                                                onKeyDown={handleKeyDown}
                                                disabled={loading}
                                                placeholder="Enter your password"
                                                autoComplete="current-password"
                                                className={`${fieldClass} pr-12 ${loginError ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''
                                                    }`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-[#1a3884] transition-colors"
                                            >
                                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Forgot Password Only (Remember Me Removed) */}
                                    <div className="flex items-center justify-end text-sm">
                                        <button
                                            type="button"
                                            onClick={() => setView('forgot')}
                                            disabled={loading}
                                            className="text-[#1a3884] dark:text-[#60a5fa] hover:text-[#4f7dff] dark:hover:text-[#82b4ff] font-semibold transition-colors"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`${primaryButtonClass} relative overflow-hidden group`}
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            {loading && <RefreshCw size={16} className="animate-spin" />}
                                            {loading ? 'Signing in...' : 'Sign In'}
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#1a3884] via-[#2450b8] to-[#5bc3f0] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>
                                </form>

                                <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-300">
                                    Facing any problem?{' '}
                                    <button
                                        type="button"
                                        onClick={() => setShowContactModal(true)}
                                        className="text-[#1a3884] dark:text-[#60a5fa] hover:text-[#4f7dff] dark:hover:text-[#82b4ff] font-semibold transition-colors"
                                    >
                                        Contact Admin
                                    </button>
                                </p>

                                {/* Student Redirect Notice - Always visible after 3s */}
                                <AnimatePresence>
                                    {showStudentAlert && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                            className="mt-3.5 pt-2.5 border-t border-[#e7eef6]"
                                        >
                                            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl p-2.5 px-3.5 border border-amber-200 dark:border-amber-900/40 flex items-start gap-3 text-left">
                                                <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={18} />
                                                <div>
                                                    <h4 className="text-xs font-bold text-amber-800 dark:text-amber-400">Are you a Student?</h4>
                                                    <p className="text-[11.5px] text-amber-700 dark:text-amber-300 mt-0.5 leading-normal">
                                                        This portal is for Admins only. Students must use the <strong className="dark:text-amber-200">SMAART Student App</strong>.
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}

                        {/* View: Two-Step Verification (OTP) */}
                        {view === 'verify' && (
                            <motion.div
                                key="verify"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="w-full"
                            >
                                <div className="mb-6">
                                    <button
                                        onClick={() => { setView('login'); setLoginOtp(''); }}
                                        className="flex items-center text-slate-500 dark:text-slate-400 hover:text-[#1a3884] dark:hover:text-[#60a5fa] transition"
                                    >
                                        <ArrowLeft size={20} className="mr-1" /> Back to Login
                                    </button>
                                </div>
                                <div className="text-center mb-8">
                                    <div className="w-16 h-16 bg-[#eef6ff] dark:bg-[#1a3884]/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#d5e6fb] dark:border-[#1a3884]/50">
                                        <ShieldCheck className="text-[#1a3884] dark:text-[#60a5fa]" size={32} />
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Two-Step Verification</h2>
                                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-6">
                                        We sent a 6-digit OTP to{' '}
                                        <span className="font-semibold text-[#1a3884] dark:text-[#60a5fa]">
                                            {formData.email
                                                ? (() => {
                                                    const [local, domain] = formData.email.split('@');
                                                    const masked = local.slice(0, 2) + '*'.repeat(Math.max(local.length - 2, 1));
                                                    return `${masked}@${domain}`;
                                                })()
                                                : 'your email'
                                            }
                                        </span>
                                    </p>
                                </div>

                                <form onSubmit={handleVerifySubmit} className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Enter OTP</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <KeyRound className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type="text"
                                                value={loginOtp}
                                                onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                inputMode="numeric"
                                                autoFocus
                                                maxLength={6}
                                                placeholder="Enter 6-digit OTP"
                                                className={`${fieldClass} text-center tracking-widest text-lg font-mono`}
                                            />
                                        </div>
                                        <div className="flex justify-end">
                                            {canResend ? (
                                                <button type="button" onClick={handleResendOtp} className="text-[#1a3884] dark:text-[#60a5fa] hover:text-[#4f7dff] dark:hover:text-[#82b4ff] text-sm font-semibold flex items-center transition-colors">
                                                    <RefreshCw size={14} className="mr-1" /> Resend OTP
                                                </button>
                                            ) : (
                                                <span className="text-slate-400 dark:text-slate-500 text-sm flex items-center">
                                                    <Timer size={14} className="mr-1" /> Resend in {otpTimer}s
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading || loginOtp.length < 6}
                                        className={`${primaryButtonClass} flex items-center justify-center gap-2`}
                                    >
                                        {loading && <RefreshCw size={16} className="animate-spin" />}
                                        {loading ? 'Verifying...' : 'Verify & Login'}
                                    </button>
                                </form>
                            </motion.div>
                        )}

                        {/* View: Forgot Password */}
                        {view === 'forgot' && (
                            <motion.div
                                key="forgot"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="w-full"
                            >
                                <div className="mb-6">
                                    <button
                                        onClick={() => setView('login')}
                                        className="flex items-center text-slate-500 dark:text-slate-400 hover:text-[#1a3884] dark:hover:text-[#60a5fa] transition"
                                    >
                                        <ArrowLeft size={20} className="mr-1" /> Back to Login
                                    </button>
                                </div>
                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Forgot Password?</h2>
                                    <p className="text-slate-600 dark:text-slate-300 leading-6">Enter your email to receive a One-Time Password (OTP)</p>
                                </div>

                                <form onSubmit={handleForgotSubmit} className="space-y-6" noValidate>
                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Email</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Mail className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type="text"
                                                value={resetEmail}
                                                onChange={(e) => setResetEmail(e.target.value)}
                                                placeholder="Enter your registered email"
                                                autoComplete="email"
                                                className={fieldClass}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={primaryButtonClass}
                                    >
                                        {loading ? 'Sending...' : 'Send OTP'}
                                    </button>
                                </form>
                            </motion.div>
                        )}

                        {/* View: Reset Password */}
                        {view === 'reset' && (
                            <motion.div
                                key="reset"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="w-full"
                            >
                                <div className="mb-6">
                                    <button
                                        onClick={() => setView('forgot')}
                                        className="flex items-center text-slate-500 dark:text-slate-400 hover:text-[#1a3884] dark:hover:text-[#60a5fa] transition"
                                    >
                                        <ArrowLeft size={20} className="mr-1" /> Back to Email
                                    </button>
                                </div>
                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Reset Password</h2>
                                    <p className="text-slate-600 dark:text-slate-300 leading-6">Enter OTP and create a strong password.</p>
                                </div>

                                <form onSubmit={handleResetSubmit} className="space-y-6">
                                    {/* OTP */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Enter OTP</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <KeyRound className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type="text"
                                                value={resetOtp}
                                                onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                inputMode="numeric"
                                                autoFocus
                                                maxLength={6}
                                                placeholder="Enter 6-digit OTP"
                                                className={fieldClass}
                                            />
                                        </div>
                                        <div className="flex justify-end">
                                            {canResend ? (
                                                <button type="button" onClick={handleResendOtp} className="text-[#1a3884] dark:text-[#60a5fa] hover:text-[#4f7dff] dark:hover:text-[#82b4ff] text-sm font-semibold flex items-center transition-colors">
                                                    <RefreshCw size={14} className="mr-1" /> Resend OTP
                                                </button>
                                            ) : (
                                                <span className="text-slate-400 dark:text-slate-500 text-sm flex items-center">
                                                    <Timer size={14} className="mr-1" /> Resend in {otpTimer}s
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* New Password with Strength Meter */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">New Password</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Lock className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="New Password"
                                                className={`${fieldClass} pr-12`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-[#1a3884]"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                        {/* Strength Bar */}
                                        {newPassword && (
                                            <div className="flex gap-1 h-1.5 mt-2">
                                                {[...Array(4)].map((_, i) => (
                                                    <div
                                                        key={i}
                                                        className={`flex-1 rounded-full transition-colors duration-300 ${i < getPasswordStrength(newPassword)
                                                            ? (getPasswordStrength(newPassword) < 2 ? 'bg-red-400' : getPasswordStrength(newPassword) < 3 ? 'bg-yellow-400' : 'bg-green-500')
                                                            : 'bg-gray-200'
                                                            }`}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {newPassword && (
                                            <p className="text-xs text-right text-slate-500 dark:text-slate-400">
                                                {['Weak', 'Fair', 'Good', 'Strong'][getPasswordStrength(newPassword) - 1] || 'Very Weak'} — need at least 'Good'
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">Confirm Password</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Lock className="text-slate-400" size={20} />
                                            </div>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="Confirm New Password"
                                                className={`${fieldClass} ${confirmPassword
                                                    ? (newPassword === confirmPassword ? 'border-green-400' : 'border-red-300')
                                                    : ''
                                                    }`}
                                            />
                                        </div>
                                        {confirmPassword && (
                                            <p className={`text-xs mt-1 ${newPassword === confirmPassword ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                                {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                                            </p>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={primaryButtonClass}
                                    >
                                        {loading ? 'Resetting...' : 'Reset Password'}
                                    </button>
                                </form>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>

            {/* ── Contact Admin Modal ── */}
            {showContactModal && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backgroundColor: 'rgba(0,21,46,0.75)', backdropFilter: 'blur(6px)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowContactModal(false); }}
                >
                    <div style={{ width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', background: 'white', borderRadius: '20px', boxShadow: '0 30px 80px rgba(0,0,0,0.3)', border: '1px solid rgba(0,0,0,0.08)' }}>
                        {/* Modal Header */}
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #002147 0%, #1a3884 100%)', borderRadius: '20px 20px 0 0' }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'white' }}>Contact Administrator</h2>
                                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Submit a support request — we'll respond to your email</p>
                            </div>
                            <button onClick={() => setShowContactModal(false)} style={{ padding: '8px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center' }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>
                            {contactSubmitted ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <CheckCircle size={56} style={{ color: '#22c55e', margin: '0 auto 16px', display: 'block' }} />
                                    <h3 style={{ color: '#0f172a', fontWeight: 700, fontSize: '20px', marginBottom: '8px' }}>Request Submitted!</h3>
                                    <p style={{ color: '#64748b', fontSize: '14px' }}>We'll get back to you at <strong>{contactForm.email}</strong> shortly.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleContactSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} noValidate>
                                    {contactErrors.submit && (
                                        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '14px' }}>
                                            {contactErrors.submit}
                                        </div>
                                    )}

                                    {/* Name + Email */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Your Name <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={contactForm.name}
                                                onChange={e => { setContactForm(p => ({ ...p, name: e.target.value })); if (contactErrors.name) setContactErrors(p => ({ ...p, name: null })); }}
                                                placeholder="Full Name"
                                                style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${contactErrors.name ? '#ef4444' : '#e2e8f0'}`, borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', color: '#0f172a' }}
                                            />
                                            {contactErrors.name && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{contactErrors.name}</p>}
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                Email <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="email"
                                                value={contactForm.email}
                                                onChange={e => { setContactForm(p => ({ ...p, email: e.target.value })); if (contactErrors.email) setContactErrors(p => ({ ...p, email: null })); }}
                                                placeholder="you@email.com"
                                                style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${contactErrors.email ? '#ef4444' : '#e2e8f0'}`, borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', color: '#0f172a' }}
                                            />
                                            {contactErrors.email && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{contactErrors.email}</p>}
                                        </div>
                                    </div>

                                    {/* Category */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Issue Type <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                            {[
                                                { value: 'account', label: 'Account', desc: 'Login, profile, or settings' },
                                                { value: 'course issue', label: 'Course Issue', desc: 'Course access, content, and materials' },
                                                { value: 'assessment issue', label: 'Assessment Issue', desc: 'Assessment access and evaluation' },
                                                { value: 'career direction issue', label: 'Career Direction Issue', desc: 'Career Direction guidance and support' },
                                                { value: 'other', label: 'Other', desc: 'General inquiries' }
                                            ].map(cat => (
                                                <button
                                                    key={cat.value}
                                                    type="button"
                                                    onClick={() => { setContactForm(p => ({ ...p, category: cat.value })); if (contactErrors.category) setContactErrors(p => ({ ...p, category: null })); }}
                                                    style={{
                                                        padding: '10px 12px',
                                                        borderRadius: '10px',
                                                        border: `2px solid ${contactForm.category === cat.value ? '#1a3884' : '#e2e8f0'}`,
                                                        background: contactForm.category === cat.value ? '#eff6ff' : 'white',
                                                        cursor: 'pointer',
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    <span style={{ display: 'block', fontWeight: 700, fontSize: '13px', color: contactForm.category === cat.value ? '#1a3884' : '#111827' }}>{cat.label}</span>
                                                    <span style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{cat.desc}</span>
                                                </button>
                                            ))}
                                        </div>
                                        {contactErrors.category && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{contactErrors.category}</p>}
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Priority</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {[
                                                { value: 'low', label: 'Low', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                                                { value: 'medium', label: 'Medium', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
                                                { value: 'high', label: 'High', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
                                            ].map(p => (
                                                <button
                                                    key={p.value}
                                                    type="button"
                                                    onClick={() => setContactForm(prev => ({ ...prev, priority: p.value }))}
                                                    style={{
                                                        flex: 1, padding: '10px 8px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
                                                        border: `2px solid ${contactForm.priority === p.value ? p.color : '#e2e8f0'}`,
                                                        background: contactForm.priority === p.value ? p.bg : 'white',
                                                        color: contactForm.priority === p.value ? p.color : '#6b7280',
                                                        transition: 'all 0.15s'
                                                    }}
                                                >{p.label}</button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Attachments */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Attachments <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none', fontSize: '11px' }}>(Optional — PNG, JPG, WEBP, max 5MB each)</span>
                                        </label>
                                        <label
                                            htmlFor="contact-file-upload"
                                            style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                padding: '20px', border: '2px dashed #cbd5e1', borderRadius: '12px',
                                                cursor: 'pointer', background: '#f8fafc', transition: 'border-color 0.2s'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.borderColor = '#1a3884'}
                                            onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                                        >
                                            <svg width="28" height="28" fill="none" stroke="#94a3b8" strokeWidth="1.5" viewBox="0 0 24 24" style={{ marginBottom: '6px' }}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                                            </svg>
                                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Click to upload screenshots</span>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Up to 5 images</span>
                                            <input
                                                id="contact-file-upload"
                                                type="file"
                                                multiple
                                                accept="image/png,image/jpeg,image/webp"
                                                style={{ display: 'none' }}
                                                onChange={e => {
                                                    const files = Array.from(e.target.files);
                                                    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
                                                    if (valid.length < files.length) alert('Some files exceeded 5MB and were skipped.');
                                                    setContactAttachments(prev => [...prev, ...valid].slice(0, 5));
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                        {contactAttachments.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                                                {contactAttachments.map((file, idx) => (
                                                    <div key={idx} style={{ position: 'relative', width: '72px', height: '72px', borderRadius: '8px', overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
                                                        <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        <button
                                                            type="button"
                                                            onClick={() => setContactAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                            style={{ position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: 'white', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                                                        >×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Description <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <textarea
                                            value={contactForm.description}
                                            onChange={e => { setContactForm(p => ({ ...p, description: e.target.value })); if (contactErrors.description) setContactErrors(p => ({ ...p, description: null })); }}
                                            placeholder="Describe your issue in detail. Include steps to reproduce, error messages, or any relevant context..."
                                            rows={4}
                                            style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${contactErrors.description ? '#ef4444' : '#e2e8f0'}`, borderRadius: '10px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'none', color: '#0f172a', fontFamily: 'inherit' }}
                                        />
                                        {contactErrors.description && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{contactErrors.description}</p>}
                                    </div>

                                    {/* Actions */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowContactModal(false)}
                                            style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: '#64748b' }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={contactSubmitting}
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #002147 0%, #1a3884 100%)', color: 'white', cursor: contactSubmitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 700, opacity: contactSubmitting ? 0.7 : 1 }}
                                        >
                                            <Send size={15} />
                                            {contactSubmitting ? 'Submitting...' : 'Submit Request'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Login;
